/**
 * Yönetici Üye Yönetimi — Spec Bölüm 4.5.2 + S2.T5
 *
 *   GET    /api/manager/members              → liste + bekleyen davetler
 *   POST   /api/manager/members/invite       → davet et
 *   PATCH  /api/manager/members/:id/role     → rol değiştir (OWNER)
 *   DELETE /api/manager/members/:id          → sil (OWNER)
 *
 * RBAC notları:
 *  - OWNER her şeyi yapabilir
 *  - ADMIN davet edebilir (sadece ADMIN/VIEWER), rol değiştiremez/silemez
 *  - VIEWER sadece görüntüler
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Header } from '../components/Header.js';
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
} from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/auth-client.js';

type OrgRole = 'OWNER' | 'ADMIN' | 'VIEWER';

interface Member {
  id: string;
  userId: string;
  role: OrgRole;
  isActive: boolean;
  joinedAt: string;
  user: {
    id: string;
    name: string | null;
    email: string;
    emailVerified: boolean;
    lastSeenAt: string | null;
  };
}

interface PendingInvitation {
  id: string;
  email: string;
  role: OrgRole;
  expiresAt: string;
  createdAt: string;
}

interface MembersResponse {
  members: Member[];
  pendingInvitations: PendingInvitation[];
}

interface CurrentMember {
  role: OrgRole;
}

const ROLE_LABELS: Record<OrgRole, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  VIEWER: 'Görüntüleyici',
};

const ROLE_COLORS: Record<OrgRole, string> = {
  OWNER: 'bg-purple-50 text-purple-900',
  ADMIN: 'bg-blue-50 text-blue-900',
  VIEWER: 'bg-slate-100 text-slate-700',
};

export function MembersPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const { data: session, isPending: sessionPending } = useSession();
  const [data, setData] = useState<MembersResponse | null>(null);
  const [myRole, setMyRole] = useState<OrgRole | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);

  async function refresh(): Promise<void> {
    setError(null);
    try {
      const [members, org] = await Promise.all([
        api.get<MembersResponse>('/api/manager/members'),
        api.get<{ currentMember: CurrentMember }>(
          '/api/manager/organizations/organization',
        ).catch(() => null),
      ]);
      setData(members);
      if (org?.currentMember?.role) setMyRole(org.currentMember.role);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Üyeler yüklenemedi');
    }
  }

  useEffect(() => {
    if (sessionPending) return;
    if (!session?.user) {
      setLocation('/yonetici/giris');
      return;
    }
    void refresh();
  }, [session, sessionPending, setLocation]);

  const canInvite = myRole === 'OWNER' || myRole === 'ADMIN';
  const canManage = myRole === 'OWNER';

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-5xl px-6 py-12">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <button
              type="button"
              onClick={() => setLocation('/yonetici/dashboard')}
              className="text-sm font-medium text-blue-900 hover:underline"
            >
              ← Panel
            </button>
            <h1 className="mt-2 text-3xl font-bold text-slate-900">
              Ekip Üyeleri
            </h1>
            <p className="mt-1 text-slate-600">
              Firma yöneticilerini ve yetkilerini yönetin. Aday hesaplarıyla
              karıştırmayın — buradakiler sadece sınav oluşturan ve sonuçları
              gören kişilerdir.
            </p>
          </div>
          {canInvite && (
            <Button onClick={() => setShowInvite(true)}>+ Üye Davet Et</Button>
          )}
        </div>

        {error && (
          <div className="mb-6">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}

        {showInvite && canInvite && (
          <InviteModal
            myRole={myRole!}
            onClose={() => setShowInvite(false)}
            onSuccess={async () => {
              setShowInvite(false);
              await refresh();
            }}
          />
        )}

        {/* Aktif üyeler */}
        <Card>
          <h2 className="text-base font-semibold text-slate-900">Aktif Üyeler</h2>
          {!data ? (
            <p className="mt-2 text-slate-500">Yükleniyor…</p>
          ) : data.members.length === 0 ? (
            <p className="mt-2 text-slate-500">Henüz üye yok.</p>
          ) : (
            <ul className="mt-4 divide-y divide-slate-100">
              {data.members.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  myUserId={session?.user.id ?? ''}
                  canManage={canManage}
                  onChanged={refresh}
                />
              ))}
            </ul>
          )}
        </Card>

        {/* Bekleyen davetler */}
        {data && data.pendingInvitations.length > 0 && (
          <Card className="mt-6">
            <h2 className="text-base font-semibold text-slate-900">
              Bekleyen Davetler ({data.pendingInvitations.length})
            </h2>
            <ul className="mt-4 divide-y divide-slate-100">
              {data.pendingInvitations.map((inv) => {
                const expiry = new Date(inv.expiresAt);
                const daysLeft = Math.max(
                  0,
                  Math.ceil(
                    (expiry.getTime() - Date.now()) / (24 * 60 * 60 * 1000),
                  ),
                );
                return (
                  <li
                    key={inv.id}
                    className="flex flex-wrap items-center justify-between gap-3 py-3"
                  >
                    <div>
                      <p className="font-medium text-slate-900">{inv.email}</p>
                      <p className="text-xs text-slate-500">
                        Gönderildi:{' '}
                        {new Date(inv.createdAt).toLocaleDateString('tr-TR')} ·
                        Kalan {daysLeft} gün
                      </p>
                    </div>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-medium ${ROLE_COLORS[inv.role]}`}
                    >
                      {ROLE_LABELS[inv.role]}
                    </span>
                  </li>
                );
              })}
            </ul>
          </Card>
        )}
      </main>
    </div>
  );
}

// ─── Member Row ─────────────────────────────────────────────────

function MemberRow({
  member,
  myUserId,
  canManage,
  onChanged,
}: {
  member: Member;
  myUserId: string;
  canManage: boolean;
  onChanged: () => void | Promise<void>;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMe = member.user.id === myUserId;
  const isOwner = member.role === 'OWNER';

  async function changeRole(newRole: OrgRole): Promise<void> {
    if (newRole === member.role) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/manager/members/${member.id}/role`, {
        role: newRole,
      });
      await onChanged();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Rol güncellenemedi');
    } finally {
      setBusy(false);
    }
  }

  async function removeMember(): Promise<void> {
    if (!confirm(`${member.user.email} ekipten çıkarılsın mı?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/api/manager/members/${member.id}`);
      await onChanged();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Üye silinemedi');
    } finally {
      setBusy(false);
    }
  }

  const [showTransfer, setShowTransfer] = useState(false);

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-slate-900">
            {member.user.name ?? member.user.email}
            {isMe && (
              <span className="ml-2 text-xs font-normal text-slate-500">
                (siz)
              </span>
            )}
          </p>
          {!member.user.emailVerified && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-900">
              Doğrulanmamış
            </span>
          )}
        </div>
        <p className="truncate text-xs text-slate-500">{member.user.email}</p>
      </div>

      <div className="flex items-center gap-2">
        {canManage && !isOwner && !isMe ? (
          <select
            value={member.role}
            onChange={(e) => changeRole(e.target.value as OrgRole)}
            disabled={busy}
            className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ADMIN">Admin</option>
            <option value="VIEWER">Görüntüleyici</option>
          </select>
        ) : (
          <span
            className={`rounded-full px-3 py-1 text-xs font-medium ${ROLE_COLORS[member.role]}`}
          >
            {ROLE_LABELS[member.role]}
          </span>
        )}
        {canManage && !isOwner && !isMe && (
          <>
            <button
              type="button"
              onClick={() => setShowTransfer(true)}
              disabled={busy}
              className="rounded-lg border border-blue-200 px-3 py-1.5 text-sm text-blue-900 hover:bg-blue-50 disabled:opacity-50"
              title="Owner'lığı bu üyeye devret"
            >
              👑 Devret
            </button>
            <button
              type="button"
              onClick={removeMember}
              disabled={busy}
              className="rounded-lg border border-red-200 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Sil
            </button>
          </>
        )}
      </div>

      {error && (
        <p className="basis-full text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {showTransfer && (
        <TransferOwnershipModal
          targetMemberId={member.id}
          targetEmail={member.user.email}
          onClose={() => setShowTransfer(false)}
          onSuccess={async () => {
            setShowTransfer(false);
            await onChanged();
          }}
        />
      )}
    </li>
  );
}

// ─── Owner Transfer Modal ───────────────────────────────────────

function TransferOwnershipModal({
  targetMemberId,
  targetEmail,
  onClose,
  onSuccess,
}: {
  targetMemberId: string;
  targetEmail: string;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}): JSX.Element {
  const [step, setStep] = useState<'initiate' | 'confirm'>('initiate');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function initiate(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/manager/transfer-ownership/initiate', {
        newOwnerMemberId: targetMemberId,
      });
      setInfo(
        'E-posta adresinize 6 haneli onay kodu gönderildi. Kod 15 dakika geçerli.',
      );
      setStep('confirm');
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Devir başlatılamadı');
    } finally {
      setBusy(false);
    }
  }

  async function confirmCode(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/manager/transfer-ownership/confirm', {
        newOwnerMemberId: targetMemberId,
        confirmCode: code,
      });
      await onSuccess();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Kod doğrulanamadı');
    } finally {
      setBusy(false);
    }
  }

  async function cancel(): Promise<void> {
    try {
      await api.post('/api/manager/transfer-ownership/cancel').catch(() => {});
    } catch {
      // ignore
    }
    onClose();
  }

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-md">
        <h3 className="text-lg font-semibold text-slate-900">
          Owner'lığı Devret
        </h3>
        <p className="mt-1 text-sm text-slate-700">
          <strong>{targetEmail}</strong> bu firmanın yeni Owner'ı olacak.
          Siz Admin rolüne düşersiniz. Bu işlem 2 aşamalı doğrulama gerektirir.
        </p>

        {error && (
          <div className="mt-4">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}
        {info && !error && (
          <div className="mt-4">
            <Alert variant="info">{info}</Alert>
          </div>
        )}

        {step === 'initiate' && (
          <div className="mt-6 space-y-3">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <strong>Geri alınamaz işlem:</strong> Bu firmanın tüm yönetimi
              hedef üyeye geçer.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose} disabled={busy}>
                İptal
              </Button>
              <Button
                onClick={initiate}
                loading={busy}
                variant="danger"
              >
                Kod Gönder
              </Button>
            </div>
          </div>
        )}

        {step === 'confirm' && (
          <form onSubmit={confirmCode} className="mt-6">
            <Field label="6 Haneli Onay Kodu" htmlFor="confirm-code" required>
              <Input
                id="confirm-code"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                className="text-center text-lg tracking-[0.5em]"
                placeholder="123456"
                autoComplete="one-time-code"
              />
            </Field>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={cancel}
                disabled={busy}
              >
                Vazgeç
              </Button>
              <Button
                type="submit"
                loading={busy}
                disabled={code.length !== 6}
                variant="danger"
              >
                Devri Onayla
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}

// ─── Invite Modal ───────────────────────────────────────────────

function InviteModal({
  myRole,
  onClose,
  onSuccess,
}: {
  myRole: OrgRole;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}): JSX.Element {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgRole>('VIEWER');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Spec 4.5.2: ADMIN sadece ADMIN/VIEWER davet edebilir; OWNER zaten transfer ile
  const allowedRoles: OrgRole[] = myRole === 'OWNER' ? ['ADMIN', 'VIEWER'] : ['ADMIN', 'VIEWER'];

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/api/manager/members/invite', { email, role });
      await onSuccess();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Davet gönderilemedi');
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-md">
        <h3 className="text-lg font-semibold text-slate-900">Üye Davet Et</h3>
        <p className="mt-1 text-sm text-slate-600">
          Davet linki e-posta ile gönderilir, 7 gün geçerlidir.
        </p>

        {error && (
          <div className="mt-4">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}

        <form onSubmit={submit} className="mt-4" noValidate>
          <Field label="E-posta" htmlFor="invite-email" required>
            <Input
              id="invite-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
              required
            />
          </Field>

          <Field
            label="Rol"
            htmlFor="invite-role"
            required
            hint="Admin sınav oluşturup davet edebilir. Görüntüleyici sadece raporları görür."
          >
            <select
              id="invite-role"
              value={role}
              onChange={(e) => setRole(e.target.value as OrgRole)}
              className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {allowedRoles.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </Field>

          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
              İptal
            </Button>
            <Button type="submit" loading={submitting} disabled={!email}>
              Davet Gönder
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
